import { useEffect, useState } from "react";
import { addCity, deleteCities, getAllCities } from "../service/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Plus, RefreshCw, Trash2, AlertCircle } from "lucide-react";

const GUJARAT_CITIES = [
    "Ahmedabad","Amreli","Anand","Ankleshwar","Bharuch","Bhavnagar","Bhuj","Dahod","Deesa",
    "Dharampur","Dholka","Gandhinagar","Godhra","Himatnagar","Idar","Jamnagar","Junagadh",
    "Kadi","Kalavad","Kalol","Kapadvanj","Karjan","Keshod","Khambhalia","Khambhat","Kheda",
    "Khedbrahma","Kheralu","Kodinar","Lathi","Limbdi","Lunawada","Mahesana","Mahuva",
    "Manavadar","Mandvi","Mangrol","Mansa","Mehmedabad","Modasa","Morbi","Nadiad","Navsari",
    "Padra","Palanpur","Palitana","Pardi","Patan","Petlad","Porbandar","Radhanpur","Rajkot",
    "Rajpipla","Rajula","Ranavav","Rapar","Salaya","Sanand","Savarkundla","Sidhpur","Sihor",
    "Songadh","Surat","Talaja","Thangadh","Tharad","Umbergaon","Umreth","Una","Unjha",
    "Upleta","Vadnagar","Vadodara","Valsad","Vapi","Veraval","Vijapur","Viramgam","Visnagar",
    "Vyara","Wadhwan","Wankaner","Adalaj","Alang","Ambaji","Ambaliyasan","Anjar","Anklav",
    "Antaliya",
];

const City = () => {
    const [addModal, setAddModal] = useState(false);
    const [cities, setCities] = useState([]);
    const [deleteModal, setDeleteModal] = useState(false);
    const [cityId, setCityId] = useState<any>();
    const [cityName, setCityName] = useState('');
    const [country] = useState('India');
    const [state] = useState('Gujarat');
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        getCities();
    }, []);

    const getCities = async () => {
        setLoading(true);
        try {
            let res = await getAllCities();
            setCities(res.data.data);
        } finally {
            setLoading(false);
        }
    }

    const handleOpenForm = () => {
        setAddModal(true);
        setCityName('');
        setErrors({});
    };

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};
        if (!cityName) {
            newErrors.cityName = "Please select a city";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (event: any) => {
        event.preventDefault();
        if (!validateForm()) return;
        try {
            let body = { country, state, name: cityName };
            await addCity(body);
            toast.success("City added successfully");
            getCities();
            setAddModal(false);
        } catch (err) {
            toast.error("Failed to add city");
        }
    };

    const handleDelete = (data: any) => {
        setDeleteModal(true);
        setCityId(data.id);
    };

    const deleteCity = async () => {
        try {
            let body = { id: cityId };
            await deleteCities(body);
            toast.success("City deleted");
            setDeleteModal(false);
            getCities();
        } catch (err) {
            toast.error("Failed to delete city");
        }
    }

    return (
        <div className="w-full max-w-6xl mx-auto pb-8 space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-foreground">Cities</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Manage cities used when onboarding partners and filtering catalog data.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => getCities()}>
                        <RefreshCw className="h-4 w-4" />
                    </Button>
                    <Button size="sm" onClick={handleOpenForm}>
                        <Plus className="h-4 w-4 mr-1" /> Add city
                    </Button>
                </div>
            </div>

            {/* Table card */}
            <Card className="overflow-hidden">
                {loading ? (
                    <div className="flex min-h-[200px] items-center justify-center">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-12">#</TableHead>
                                    <TableHead>Country</TableHead>
                                    <TableHead>State</TableHead>
                                    <TableHead>City</TableHead>
                                    <TableHead className="text-right w-24">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {cities.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center text-muted-foreground py-14">
                                            No cities yet. Add one using the button above.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    cities.map((item: any, index: number) => (
                                        <TableRow key={item.id}>
                                            <TableCell className="text-muted-foreground">{index + 1}</TableCell>
                                            <TableCell>{item.country}</TableCell>
                                            <TableCell>{item.state}</TableCell>
                                            <TableCell className="font-medium">{item.name}</TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-destructive hover:text-destructive"
                                                    onClick={() => handleDelete(item)}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </Card>

            {/* Add city dialog */}
            <Dialog open={addModal} onOpenChange={setAddModal}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Add city</DialogTitle>
                        <DialogDescription>
                            Select a city in Gujarat to add to the platform.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-1.5">
                            <Label>Country</Label>
                            <Input readOnly value={country} className="bg-muted/50" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>State</Label>
                            <Input readOnly value={state} className="bg-muted/50" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>City</Label>
                            <select
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                value={cityName}
                                onChange={(e) => setCityName(e.target.value)}
                            >
                                <option value="">Select City</option>
                                {GUJARAT_CITIES.map((city) => (
                                    <option key={city} value={city}>{city}</option>
                                ))}
                            </select>
                            {errors.cityName && (
                                <p className="text-destructive text-xs">{errors.cityName}</p>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAddModal(false)}>Cancel</Button>
                        <Button onClick={handleSubmit}>Save city</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirmation dialog */}
            <Dialog open={deleteModal} onOpenChange={setDeleteModal}>
                <DialogContent className="max-w-sm">
                    <DialogHeader className="items-center text-center">
                        <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-2" />
                        <DialogTitle>Delete this city?</DialogTitle>
                        <DialogDescription>
                            Partners referencing this city may need to be updated separately.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex-row justify-center gap-2 sm:justify-center">
                        <Button variant="outline" onClick={() => setDeleteModal(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={deleteCity}>Delete</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default City;
